// src/pages/VerifyEmailPending.jsx
//
// Lets someone resume signup email verification if they closed the tab (or
// their session lapsed) during Step 3 of Signup.jsx, before ever entering
// the code. Previously flagged but not built — see
// kailux-master-plan.md item 16: "A firm that closes Step 3 of Signup
// without entering the code has no way to resume from within the app —
// ProtectedRoute.jsx only checks auth, so they land on the dashboard and
// just hit 403s on AI-cost actions with no visible fix."
//
// Reached via a small persistent banner (EmailVerificationBanner, rendered
// by ProtectedRoute) whenever a real firm exists but
// firm.email_verified_at is still null — deliberately NOT a hard redirect,
// mirroring PracticeVerificationPending's own "explore while pending"
// philosophy: email verification only actually blocks AI-cost/generation
// actions server-side (_require_account_verified in main.py), not general
// navigation, so this page shouldn't either.
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { verifySignupCode, resendSignupCode } from '../api/kailux'
import ErrorMessage from '../components/ErrorMessage'
import { Mail } from 'lucide-react'


import { T, alpha } from '../theme/theme'
export default function VerifyEmailPending() {
  const { firm, user, loadFirm } = useAuth()
  const navigate = useNavigate()

  const [code,      setCode]      = useState('')
  const [verifying, setVerifying] = useState(false)
  const [error,     setError]     = useState('')
  const [resending, setResending] = useState(false)
  const [resendMsg, setResendMsg] = useState('')

  // Already verified (e.g. resolved in another tab) — nothing to do here.
  if (firm?.email_verified_at) {
    navigate('/dashboard')
    return null
  }

  const handleVerify = async () => {
    if (!firm?.id) return
    setVerifying(true); setError('')
    try {
      await verifySignupCode(firm.id, code.trim())
      if (user?.id) await loadFirm(user.id)
      navigate('/dashboard')
    } catch (err) {
      setError(err.response?.data?.detail || 'Incorrect code. Please try again.')
      setVerifying(false)
    }
  }

  const handleResend = async () => {
    if (!firm?.id) return
    setResending(true); setResendMsg(''); setError('')
    try {
      await resendSignupCode(firm.id)
      setResendMsg('A new code is on its way — check your inbox.')
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not send a new code. Please try again shortly.')
    } finally {
      setResending(false)
    }
  }

  return (
    <div style={{ minHeight:'100vh', background:T.bg, color:T.tx,
      fontFamily:"'Outfit',sans-serif", display:'flex', alignItems:'center',
      justifyContent:'center', padding:'24px' }}>
      <div style={{ width:'100%', maxWidth:'480px', background:T.s1,
        border:`1px solid ${T.bd}`, borderRadius:'16px', padding:'32px' }}>

        <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'8px' }}>
          <Mail size={22} color={T.or} />
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'20px', fontWeight:'800' }}>
            Verify your email
          </div>
        </div>

        <p style={{ fontSize:'14px', color:T.mt, lineHeight:'1.7', marginBottom:'20px' }}>
          Your signup was interrupted before you entered your verification code. Enter
          it below to unlock AI-powered features — you can browse KaiLux in the
          meantime, but AFS generation, note drafting, and other AI actions stay on
          hold until this is done.
        </p>

        {error && <ErrorMessage style={{ marginBottom:'16px' }}>{error}</ErrorMessage>}

        <div style={{ marginBottom:'14px' }}>
          <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
            color:T.lt, letterSpacing:'1px', marginBottom:'6px' }}>
            VERIFICATION CODE
          </label>
          <input type="text" inputMode="numeric" maxLength={6} value={code}
            onChange={e=>setCode(e.target.value.replace(/\D/g,''))}
            placeholder="123456" autoFocus
            style={{ width:'100%', padding:'14px', background:T.s3,
              border:`1px solid ${T.bd}`, borderRadius:'8px', color:T.tx,
              fontSize:'22px', fontWeight:'700', letterSpacing:'0.3em',
              textAlign:'center', fontFamily:'monospace',
              outline:'none', boxSizing:'border-box' }}/>
        </div>

        <div style={{ fontSize:'13px', color:T.mt, marginBottom:'20px' }}>
          Didn't get it?{' '}
          <button type="button" onClick={handleResend} disabled={resending}
            style={{ background:'none', border:'none', color:T.or,
              fontSize:'13px', fontFamily:"'Outfit',sans-serif",
              cursor: resending ? 'not-allowed' : 'pointer', padding:0,
              textDecoration:'underline' }}>
            {resending ? 'Sending…' : 'Resend code'}
          </button>
        </div>
        {resendMsg && <div style={{ fontSize:'13px', color:T.gn, marginBottom:'20px' }}>{resendMsg}</div>}

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <button onClick={() => navigate('/dashboard')}
            style={{ background:'none', border:'none', color:T.mt, fontSize:'13px', cursor:'pointer' }}>
            Continue exploring KaiLux →
          </button>
          <button onClick={handleVerify}
            disabled={verifying || code.trim().length !== 6}
            style={{ padding:'12px 28px', borderRadius:'8px', border:'none',
              background:(verifying||code.trim().length!==6) ? T.mt
                : T.prg,
              color:T.onpri, fontSize:'14px', fontWeight:'700',
              cursor:(verifying||code.trim().length!==6) ? 'not-allowed' : 'pointer' }}>
            {verifying ? 'Verifying…' : 'Verify →'}
          </button>
        </div>
      </div>
    </div>
  )
}
